/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/**
* Test if a value is an array-like object containing only safe integers.
*
* @module @stdlib/assert-is-safe-integer-array
*
* @example
* import isSafeIntegerArray from '@stdlib/assert-is-safe-integer-array';
*
* var bool = isSafeIntegerArray( [ -3.0, new Number(0.0), 2.0 ] );
* // returns true
*
* bool = isSafeIntegerArray( [ -3.0, '3.0' ] );
* // returns false
*
* @example
* import { primitives as isSafeIntegerArray } from '@stdlib/assert-is-safe-integer-array';
*
* var bool = isSafeIntegerArray( [ -1.0, 10.0 ] );
* // returns true
*
* bool = isSafeIntegerArray( [ -1.0, 0.0, 5.0 ] );
* // returns true
*
* bool = isSafeIntegerArray( [ -3.0, new Number(-1.0) ] );
* // returns false
*
* @example
* import { objects as isSafeIntegerArray } from '@stdlib/assert-is-safe-integer-array';
*
* var bool = isSafeIntegerArray( [ new Number(1.0), new Number(3.0) ] );
* // returns true
*
* bool = isSafeIntegerArray( [ -1.0, 0.0, 3.0 ] );
* // returns false
*
* bool = isSafeIntegerArray( [ 3.0, new Number(-1.0) ] );
* // returns false
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import arrayfun from '@stdlib/assert-tools-array-like-function';
import isSafeInteger from '@stdlib/assert-is-safe-integer';


// VARIABLES //

var isPrimitiveArray = arrayfun( isSafeInteger.isPrimitive );
var isObjectArray = arrayfun( isSafeInteger.isObject );


// MAIN //

var isSafeIntegerArray = arrayfun( isSafeInteger );
setReadOnly( isSafeIntegerArray, 'primitives', isPrimitiveArray );
setReadOnly( isSafeIntegerArray, 'objects', isObjectArray );


// EXPORTS //

export default isSafeIntegerArray;
